import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Plus, X, Loader2 } from 'lucide-react'
import { useCreateCrawl } from '../hooks/useCrawls'
import { cn } from '@/lib/utils'

const PAGE_PRESETS = [
  { label: 'Quick scan', value: 50, description: 'Top pages only' },
  { label: 'Standard', value: 200, description: 'Most sites' },
  { label: 'Deep audit', value: 500, description: 'Large sites' },
  { label: 'Full site', value: 2000, description: 'Everything' },
] as const

const RATE_PRESETS = [
  { label: 'Gentle', value: 1, description: '1 req/s' },
  { label: 'Normal', value: 2, description: '2 req/s' },
  { label: 'Aggressive', value: 5, description: '5 req/s' },
] as const

export function NewCrawlPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const createCrawl = useCreateCrawl()

  // Pre-fill from query params (used by Recrawl button)
  const prefillUrl = searchParams.get('url') ?? ''
  const prefillMaxPages = Number(searchParams.get('maxPages')) || 200
  const prefillRateLimit = Number(searchParams.get('rateLimit')) || 2
  // Default respectRobots to true unless the recrawl button explicitly passes "false".
  // Ethically and for client-site safety the default should honour robots.txt.
  const prefillRespectRobots = searchParams.has('respectRobots')
    ? searchParams.get('respectRobots') === 'true'
    : true
  const isPresetPages = PAGE_PRESETS.some((p) => p.value === prefillMaxPages)

  const [mode, setMode] = useState<'spider' | 'list'>('spider')
  const [url, setUrl] = useState(prefillUrl)
  const [urlList, setUrlList] = useState('')
  const [loadingSitemap, setLoadingSitemap] = useState(false)
  const [maxPages, setMaxPages] = useState(isPresetPages ? prefillMaxPages : 200)
  const [customPages, setCustomPages] = useState(isPresetPages ? '' : String(prefillMaxPages))
  const [isCustomPages, setIsCustomPages] = useState(!isPresetPages && prefillMaxPages !== 200)
  const [rateLimit, setRateLimit] = useState(prefillRateLimit)
  const [respectRobots, setRespectRobots] = useState(prefillRespectRobots)
  const [urlBlurred, setUrlBlurred] = useState(false)
  const [checkExternalLinks, setCheckExternalLinks] = useState(false)
  const [enableJsRendering, setEnableJsRendering] = useState(true)
  const [enableScreenshots, setEnableScreenshots] = useState(true)
  const [enableAiAnalysis, setEnableAiAnalysis] = useState(true)
  const [extractionRules, setExtractionRules] = useState<Array<{ label: string; selector: string }>>([])

  const effectiveMaxPages = isCustomPages
    ? Math.max(10, Math.min(5000, Number(customPages) || 200))
    : maxPages

  // Inline URL validity — accepts with-or-without scheme, rejects plain tokens like "example"
  const normalisedUrl = url.trim().match(/^https?:\/\//) ? url.trim() : `https://${url.trim()}`
  const urlIsValid = (() => {
    if (!url.trim()) return false
    try {
      const u = new URL(normalisedUrl)
      // Require at least one dot in the hostname (rejects localhost-style, single-label tokens)
      return /\./.test(u.hostname) && u.hostname.length >= 3
    } catch {
      return false
    }
  })()
  const urlError = urlBlurred && url.trim() && !urlIsValid
    ? 'Enter a full URL, e.g. https://example.com.au'
    : null

  // Parse URL list for list mode
  const parsedUrls = urlList
    .split('\n')
    .map((u) => u.trim())
    .filter((u) => u.length > 0 && (u.startsWith('http://') || u.startsWith('https://')))

  const handleLoadFromSitemap = async () => {
    let sitemapUrl = url.trim()
    if (!sitemapUrl.startsWith('http://') && !sitemapUrl.startsWith('https://')) {
      sitemapUrl = `https://${sitemapUrl}`
    }
    // Try common sitemap locations
    const base = sitemapUrl.replace(/\/$/, '')
    const candidates = [`${base}/sitemap.xml`, `${base}/sitemap_index.xml`]

    setLoadingSitemap(true)
    try {
      for (const candidateUrl of candidates) {
        const res = await fetch(candidateUrl)
        if (res.ok) {
          const text = await res.text()
          // Extract <loc> URLs from sitemap XML
          const locs = [...text.matchAll(/<loc>(.*?)<\/loc>/gi)].map((m) => (m[1] ?? '').trim())
          if (locs.length > 0) {
            // Filter out sitemap index references (ending in .xml)
            const pageUrls = locs.filter((u) => !u.endsWith('.xml'))
            const finalUrls = pageUrls.length > 0 ? pageUrls : locs
            setUrlList(finalUrls.slice(0, 500).join('\n'))
            break
          }
        }
      }
    } catch {
      // Silently fail — user can paste URLs manually
    } finally {
      setLoadingSitemap(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      const validRules = extractionRules.filter((r) => r.label.trim() && r.selector.trim())

      if (mode === 'list') {
        if (parsedUrls.length === 0) return
        const result = await createCrawl.mutateAsync({
          mode: 'list',
          urls: parsedUrls,
          rateLimit,
          checkExternalLinks,
          enableJsRendering,
          enableScreenshots,
          enableAiAnalysis,
          ...(validRules.length > 0 ? { extractionRules: validRules } : {}),
        })
        navigate(`/dashboard/crawls/${result.id}`)
      } else {
        // Spider mode
        let crawlUrl = url.trim()
        if (!crawlUrl.startsWith('http://') && !crawlUrl.startsWith('https://')) {
          crawlUrl = `https://${crawlUrl}`
        }
        const result = await createCrawl.mutateAsync({
          mode: 'spider',
          url: crawlUrl,
          maxPages: effectiveMaxPages,
          rateLimit,
          respectRobots,
          checkExternalLinks,
          enableJsRendering,
          enableScreenshots,
          enableAiAnalysis,
          ...(validRules.length > 0 ? { extractionRules: validRules } : {}),
        })
        navigate(`/dashboard/crawls/${result.id}`)
      }
    } catch (err) {
      // Error handled by mutation
    }
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">New Crawl</h1>
        <p className="text-muted-foreground">Start an SEO audit of a website</p>
      </div>

      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle>Crawl Settings</CardTitle>
            <CardDescription>Configure the crawl parameters</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Mode toggle */}
            <div className="space-y-2">
              <Label>Crawl Mode</Label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setMode('spider')}
                  className={cn(
                    'flex flex-col items-center rounded-md border px-3 py-2 text-sm transition-colors',
                    mode === 'spider'
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border hover:border-primary/50 hover:bg-muted'
                  )}
                >
                  <span className="font-medium">Spider</span>
                  <span className="text-xs text-muted-foreground">Discover pages from a start URL</span>
                </button>
                <button
                  type="button"
                  onClick={() => setMode('list')}
                  className={cn(
                    'flex flex-col items-center rounded-md border px-3 py-2 text-sm transition-colors',
                    mode === 'list'
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border hover:border-primary/50 hover:bg-muted'
                  )}
                >
                  <span className="font-medium">URL List</span>
                  <span className="text-xs text-muted-foreground">Analyse specific URLs</span>
                </button>
              </div>
            </div>

            {/* URL input — spider mode */}
            {mode === 'spider' && (
              <div className="space-y-2">
                <Label htmlFor="url">Website URL</Label>
                <Input
                  id="url"
                  type="text"
                  placeholder="https://example.com.au"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  onBlur={() => setUrlBlurred(true)}
                  required
                  aria-invalid={!!urlError}
                />
                {urlError ? (
                  <p className="text-xs text-destructive">{urlError}</p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Enter the homepage URL. The crawler will discover and follow internal links. <span className="text-foreground/60">More options below ↓ (max pages, speed, AI analysis, screenshots).</span>
                  </p>
                )}
              </div>
            )}

            {/* URL list — list mode */}
            {mode === 'list' && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="urlList">URLs to Analyse</Label>
                  {parsedUrls.length > 0 && (
                    <Badge variant="secondary">{parsedUrls.length} URL{parsedUrls.length !== 1 ? 's' : ''}</Badge>
                  )}
                </div>
                <textarea
                  id="urlList"
                  placeholder="https://example.com.au/page-1&#10;https://example.com.au/page-2&#10;https://example.com.au/page-3"
                  value={urlList}
                  onChange={(e) => setUrlList(e.target.value)}
                  rows={8}
                  className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 font-mono"
                />
                <div className="flex items-center justify-between">
                  <p className="text-xs text-muted-foreground">
                    One URL per line (max 500). Must include https:// or http://.
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleLoadFromSitemap}
                    disabled={loadingSitemap || !url.trim()}
                  >
                    {loadingSitemap ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                    Load from Sitemap
                  </Button>
                </div>
                {mode === 'list' && (
                  <div className="space-y-2">
                    <Label htmlFor="sitemapUrl">Sitemap domain (for Load from Sitemap)</Label>
                    <Input
                      id="sitemapUrl"
                      type="text"
                      placeholder="https://example.com.au"
                      value={url}
                      onChange={(e) => setUrl(e.target.value)}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Max Pages — preset chips (spider mode only) */}
            {mode === 'spider' && (
            <div className="space-y-3">
              <Label>Max Pages</Label>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                {PAGE_PRESETS.map((preset) => (
                  <button
                    key={preset.value}
                    type="button"
                    onClick={() => { setMaxPages(preset.value); setIsCustomPages(false) }}
                    className={cn(
                      'flex flex-col items-center rounded-md border px-3 py-2 text-sm transition-colors',
                      !isCustomPages && maxPages === preset.value
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border hover:border-primary/50 hover:bg-muted'
                    )}
                  >
                    <span className="font-medium">{preset.label}</span>
                    <span className="text-xs text-muted-foreground">{preset.value} pages</span>
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => { setIsCustomPages(true); setCustomPages(String(effectiveMaxPages)) }}
                  className={cn(
                    'flex flex-col items-center rounded-md border px-3 py-2 text-sm transition-colors',
                    isCustomPages
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border hover:border-primary/50 hover:bg-muted'
                  )}
                >
                  <span className="font-medium">Custom</span>
                  <span className="text-xs text-muted-foreground">10–5000</span>
                </button>
              </div>
              {isCustomPages && (
                <Input
                  type="number"
                  min={10}
                  max={5000}
                  value={customPages}
                  onChange={(e) => setCustomPages(e.target.value)}
                  placeholder="Enter page count"
                  className="max-w-[200px]"
                  autoFocus
                />
              )}
            </div>
            )}

            {/* Rate Limit — preset chips */}
            <div className="space-y-3">
              <Label>Crawl Speed</Label>
              <div className="grid grid-cols-3 gap-2">
                {RATE_PRESETS.map((preset) => (
                  <button
                    key={preset.value}
                    type="button"
                    onClick={() => setRateLimit(preset.value)}
                    className={cn(
                      'flex flex-col items-center rounded-md border px-3 py-2 text-sm transition-colors',
                      rateLimit === preset.value
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border hover:border-primary/50 hover:bg-muted'
                    )}
                  >
                    <span className="font-medium">{preset.label}</span>
                    <span className="text-xs text-muted-foreground">{preset.description}</span>
                  </button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Gentle is safer for shared hosting. Aggressive is faster but may trigger rate limits.
              </p>
            </div>

            {/* Respect robots.txt (spider mode only) */}
            {mode === 'spider' && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Respect robots.txt</Label>
                    <p className="text-xs text-muted-foreground">
                      Follow the site's crawling rules. Turn off for staging sites or restricted environments.
                    </p>
                  </div>
                  <Switch checked={respectRobots} onCheckedChange={setRespectRobots} />
                </div>
                {!respectRobots && (
                  <Alert variant="destructive" className="text-xs">
                    <AlertDescription>
                      <strong>Bypassing robots.txt.</strong> Only turn this off for staging or sites where you own the crawl permission. Ignoring robots.txt on a live production client site can trigger trust, legal, or hosting concerns.
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}

            {/* Check external links */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Check external links</Label>
                <p className="text-xs text-muted-foreground">
                  Verify outbound links are reachable (slower crawl).
                </p>
              </div>
              <Switch checked={checkExternalLinks} onCheckedChange={setCheckExternalLinks} />
            </div>

            {/* JS Rendering */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>JS Rendering</Label>
                <p className="text-xs text-muted-foreground">
                  Re-render JS-heavy pages with a headless browser.
                </p>
              </div>
              <Switch checked={enableJsRendering} onCheckedChange={setEnableJsRendering} />
            </div>

            {/* Page Screenshots */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Page Screenshots</Label>
                <p className="text-xs text-muted-foreground">
                  Capture a visual screenshot of each page using a headless browser.
                </p>
              </div>
              <Switch checked={enableScreenshots} onCheckedChange={setEnableScreenshots} />
            </div>

            {/* AI Analysis */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>AI Analysis</Label>
                <p className="text-xs text-muted-foreground">
                  AI analyses every page for content quality, SEO effectiveness, spelling, trust signals and accessibility.
                </p>
              </div>
              <Switch checked={enableAiAnalysis} onCheckedChange={setEnableAiAnalysis} />
            </div>

            {/* Custom Extraction Rules */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Custom Extraction Rules</Label>
                  <p className="text-xs text-muted-foreground">
                    Extract specific content using CSS selectors (e.g. prices, phone numbers)
                  </p>
                </div>
                {extractionRules.length < 10 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setExtractionRules([...extractionRules, { label: '', selector: '' }])}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Rule
                  </Button>
                )}
              </div>
              {extractionRules.map((rule, i) => (
                <div key={i} className="flex gap-2 items-start">
                  <Input
                    placeholder="Label (e.g. Price)"
                    value={rule.label}
                    onChange={(e) => {
                      const updated = [...extractionRules]
                      updated[i] = { ...rule, label: e.target.value }
                      setExtractionRules(updated)
                    }}
                    className="flex-1"
                  />
                  <Input
                    placeholder="CSS selector (e.g. .product-price)"
                    value={rule.selector}
                    onChange={(e) => {
                      const updated = [...extractionRules]
                      updated[i] = { ...rule, selector: e.target.value }
                      setExtractionRules(updated)
                    }}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setExtractionRules(extractionRules.filter((_, j) => j !== i))}
                    className="shrink-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>

            {/* Error display */}
            {createCrawl.isError && (
              <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                {typeof createCrawl.error === 'string'
                  ? createCrawl.error
                  : (createCrawl.error as Error)?.message || String(createCrawl.error) || 'Failed to start crawl'}
              </div>
            )}

            {/* Submit */}
            <Button
              type="submit"
              className="w-full"
              disabled={
                createCrawl.isPending ||
                (mode === 'spider' && !urlIsValid) ||
                (mode === 'list' && parsedUrls.length === 0)
              }
            >
              {createCrawl.isPending ? 'Starting...' : mode === 'list' ? `Analyse ${parsedUrls.length} URL${parsedUrls.length !== 1 ? 's' : ''}` : 'Start Crawl'}
            </Button>
          </CardContent>
        </Card>
      </form>
    </div>
  )
}
